/**
 * Statusstrahl im Kopf des Nachweis-Details: die Status seines Bearbeitungswegs (ADR 007,
 * `Evidence.status_steps` vom Server) als Badges in einer Reihe, verbunden durch eine Linie –
 * beim Selbst erledigen zwei, beim Anfordern drei, beim Beauftragen der volle Ablauf. „In
 * Korrektur“ steht nur darin, solange der Nachweis dort steht – im gewöhnlichen Ablauf kommt er
 * nicht vor.
 *
 * Darstellung und Verdichtung bei wenig Platz übernimmt `StatusStrahl`, dieselbe Komponente wie im
 * Kopf eines Antrags. Drei Zustände, auf einen Blick unterscheidbar:
 * - erledigt: türkis umrandet, ohne Hintergrund, normale Schrift – geschafft, aber nicht mehr
 *   im Fokus,
 * - aktuell: voll in seiner Statusfarbe, eine Stufe größer und mit leichtem Schatten – er muss
 *   sich von beiden Seiten abheben,
 * - noch offen: hellgrau mit normaler Schrift – noch nicht erreicht.
 * Bis zum aktuellen Status ist die Linie türkis, danach grau.
 *
 * Ein archivierter Nachweis hat keinen Platz im Ablauf; dort steht der Hinweis statt des Strahls.
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import StatusChip from "../../../common/display/StatusChip";
import StatusStrahl from "../../../common/display/StatusStrahl";
import { colors } from "../../../../styles/theme";
import { getEvidenceStatusVisual } from "../../../../utils/colors/evidenceStatusUtils";
import { Evidence } from "../../../../types";

interface NachweisStatusStrahlProps {
  evidence: Evidence;
}

const NachweisStatusStrahl: React.FC<NachweisStatusStrahlProps> = ({
  evidence,
}) => {
  if (evidence.archiviert) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <StatusChip
          label="Archiviert"
          color={colors.text.secondary}
          background={colors.background.tableHeader}
        />
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          Dieser Nachweis wird nicht mehr benötigt und zählt nicht in den
          Antragsfortschritt.
        </Typography>
      </Box>
    );
  }

  const steps = evidence.status_steps.filter(
    (status) => status !== "In Korrektur" || evidence.status === status,
  );
  const rang = steps.indexOf(evidence.status);
  return (
    <StatusStrahl
      ariaLabel={`Status: ${evidence.status}`}
      aktuell={rang}
      schritte={steps.map((status, index) => ({
        key: status,
        // Der Endstatus des Wegs ist grün, sobald er erreicht ist – „Geliefert“ nur beim Anfordern.
        visual: getEvidenceStatusVisual(
          status,
          evidence.completed && index === rang,
        ),
      }))}
    />
  );
};

export default NachweisStatusStrahl;
